/* ============================================================
   AUTOMATIC COLOUR DETECTION
   Reads an uploaded product image and works out which colour(s)
   the piece actually is, so the admin never has to type it in.

   Runs entirely in the browser on the local File object before
   the image is uploaded — no server, no API key, no CORS issues,
   and no extra dependency.

   Pipeline
   ────────
   1. Draw the image onto a small off-screen canvas (max 96px).
      Sampling more than that costs time and changes nothing.
   2. Throw away pixels that are almost certainly *backdrop*
      rather than product: near-white studio background, the
      soft grey drop-shadow underneath it, transparent padding.
   3. Bucket what is left into a coarse 3D colour histogram and
      score each bucket by (population × saturation weight), so a
      small vivid stone beats a large dull grey area.
   4. Merge buckets that are perceptually the same colour.
   5. Average the true pixels of each surviving bucket to get a
      clean hex, then name it.
   ============================================================ */

import { rgbToHex, rgbToLab, deltaE, rgbToHsl, nameForHex } from './colorNames';

const SAMPLE_MAX = 96;   // longest edge we sample, in px
const BUCKET_BITS = 4;   // 4 bits per channel → 16³ = 4096 buckets

/* ── Load a File / Blob / URL into an <img> ─────────────────── */
function loadImage(source) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl = null;

    img.onload = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read that image'));
    };

    if (typeof source === 'string') {
      // Remote images need CORS to be readable by canvas
      img.crossOrigin = 'anonymous';
      img.src = source;
    } else {
      objectUrl = URL.createObjectURL(source);
      img.src = objectUrl;
    }
  });
}

/* ── Is this pixel backdrop rather than product? ────────────── */
function isBackdrop(r, g, b, a) {
  if (a < 128) return true;                       // transparent padding

  const { s, l } = rgbToHsl(r, g, b);

  // Blown-out studio white / paper background
  if (l > 0.93 && s < 0.10) return true;

  // The soft grey halo a product casts on that paper. The cut-off is
  // deliberately high: silver, platinum and oxidised finishes are real
  // product colours in a jewellery catalogue, and an earlier, more
  // aggressive rule here erased them from every silver piece.
  if (s < 0.08 && l > 0.90) return true;

  // Crushed black around the edges of a cut-out
  if (l < 0.05) return true;

  return false;
}

const bucketKey = (r, g, b) => {
  const shift = 8 - BUCKET_BITS;
  return ((r >> shift) << (BUCKET_BITS * 2)) | ((g >> shift) << BUCKET_BITS) | (b >> shift);
};

/**
 * Detect the dominant colour(s) of an image.
 *
 * @param {File|Blob|string} source  local file, blob or (CORS-enabled) URL
 * @param {object} [opts]
 * @param {number} [opts.max=3]      how many colours to return
 * @param {number} [opts.minShare=0.06] ignore colours below this share
 * @returns {Promise<Array<{name:string, hex:string, pct:number}>>}
 *          sorted most-dominant first; empty array if nothing readable
 */
export async function detectColors(source, opts = {}) {
  const max = opts.max ?? 3;
  const minShare = opts.minShare ?? 0.06;

  let img;
  try {
    img = await loadImage(source);
  } catch (err) {
    console.warn('[colorDetect] image load failed:', err.message);
    return [];
  }

  const scale = Math.min(1, SAMPLE_MAX / Math.max(img.width || 1, img.height || 1));
  const w = Math.max(1, Math.round((img.width || SAMPLE_MAX) * scale));
  const h = Math.max(1, Math.round((img.height || SAMPLE_MAX) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [];

  ctx.drawImage(img, 0, 0, w, h);

  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch (err) {
    // Tainted canvas — only happens for cross-origin URLs without CORS
    console.warn('[colorDetect] canvas is not readable:', err.message);
    return [];
  }

  /* ── Build the histogram ──────────────────────────────────── */
  const buckets = new Map();   // key → { n, r, g, b, score }
  let kept = 0;
  let total = 0;

  const addPixel = (r, g, b, weight) => {
    const key = bucketKey(r, g, b);
    let bk = buckets.get(key);
    if (!bk) { bk = { n: 0, r: 0, g: 0, b: 0, score: 0 }; buckets.set(key, bk); }
    bk.n += 1;
    bk.r += r; bk.g += g; bk.b += b;
    bk.score += weight;
  };

  for (let i = 0; i < data.length; i += 4) {
    total++;
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (isBackdrop(r, g, b, a)) continue;
    kept++;

    const { s, l } = rgbToHsl(r, g, b);
    // Saturated, mid-lightness pixels describe the product best.
    const satWeight = 0.45 + s * 1.55;
    const lumWeight = 1 - Math.abs(l - 0.5) * 0.7;
    addPixel(r, g, b, satWeight * lumWeight);
  }

  /* Nothing survived the filter — the photo is probably a flat
     white or flat black product. Re-run without the backdrop
     filter so we still return something truthful. */
  if (kept < total * 0.02) {
    buckets.clear();
    kept = 0;
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 128) continue;
      kept++;
      addPixel(data[i], data[i + 1], data[i + 2], 1);
    }
  }

  if (!kept) return [];

  /* ── Rank, then merge perceptually identical buckets ──────── */
  const ranked = [...buckets.values()]
    .map((bk) => {
      const r = bk.r / bk.n, g = bk.g / bk.n, b = bk.b / bk.n;
      return { r, g, b, n: bk.n, score: bk.score, lab: rgbToLab(r, g, b) };
    })
    .sort((a, b) => b.score - a.score);

  const merged = [];
  for (const cand of ranked) {
    const twin = merged.find((m) => deltaE(m.lab, cand.lab) < 14);
    if (twin) {
      const tn = twin.n + cand.n;
      twin.r = (twin.r * twin.n + cand.r * cand.n) / tn;
      twin.g = (twin.g * twin.n + cand.g * cand.n) / tn;
      twin.b = (twin.b * twin.n + cand.b * cand.n) / tn;
      twin.n = tn;
      twin.score += cand.score;
      twin.lab = rgbToLab(twin.r, twin.g, twin.b);
    } else {
      merged.push({ ...cand });
    }
    if (merged.length >= max * 4) break;
  }

  const scoreTotal = merged.reduce((s, m) => s + m.score, 0) || 1;

  /* ── Name them, and never return the same name twice ──────── */
  const out = [];
  const seen = new Set();

  for (const m of merged) {
    const share = m.score / scoreTotal;
    if (out.length && share < minShare) continue;

    const hex = rgbToHex(m.r, m.g, m.b);
    const { name } = nameForHex(hex);
    if (seen.has(name)) continue;
    seen.add(name);

    out.push({ name, hex, pct: Math.round(share * 100) });
    if (out.length >= max) break;
  }

  return out;
}

/**
 * Detect colours across several images and merge the results, so a
 * product photographed from three angles gets one coherent palette.
 *
 * @param {Array<File|Blob|string>} sources
 * @param {object} [opts]
 * @returns {Promise<Array<{name:string, hex:string, pct:number}>>}
 */
export async function detectColorsForSet(sources, opts = {}) {
  const max = opts.max ?? 3;
  const perImage = [];

  for (const src of sources || []) {
    // Sequential on purpose: decoding several large images at once
    // on a mid-range phone is what makes an upload form feel slow.
    const res = await detectColors(src, { max: 3, minShare: 0.05 });
    if (res.length) perImage.push(res);
  }

  if (!perImage.length) return [];

  const tally = new Map();
  for (const list of perImage) {
    for (const c of list) {
      const prev = tally.get(c.name);
      if (prev) {
        prev.weight += c.pct;
        prev.hexes.push(c.hex);
      } else {
        tally.set(c.name, { name: c.name, weight: c.pct, hexes: [c.hex] });
      }
    }
  }

  const totalWeight = [...tally.values()].reduce((s, t) => s + t.weight, 0) || 1;

  return [...tally.values()]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, max)
    .map((t) => {
      // Average the hexes we saw for this name across the photos
      const sum = t.hexes.reduce((acc, hx) => {
        const n = parseInt(hx.slice(1), 16);
        acc.r += (n >> 16) & 255;
        acc.g += (n >> 8) & 255;
        acc.b += n & 255;
        return acc;
      }, { r: 0, g: 0, b: 0 });
      const k = t.hexes.length;
      return {
        name: t.name,
        hex: rgbToHex(sum.r / k, sum.g / k, sum.b / k),
        pct: Math.round((t.weight / totalWeight) * 100),
      };
    });
}

/** The single colour we quote in an order message.
    The admin-set colour (typed name, saved on `product.color`) wins; the
    colour read automatically from the photo is only a fallback. */
export function primaryColor(product) {
  if (!product) return null;
  if (product.color && product.color.name) return product.color;
  const det = product.detectedColors;
  if (Array.isArray(det) && det.length) return { name: det[0].name, hex: det[0].hex };
  return null;
}
